import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router';
import { Alert, Button, Card, IconInfoCircle, PhotoViewer, TextLink } from '@indipia/ui-core';
import {
  ShareLoadSkeleton,
  ShareNoticeScreen,
  ShareStateScreen,
  classifyShareError,
  formatMonthDayDow,
  guestFileUrl,
  useShareResource,
  type SharePhotoItem,
} from '@indipia/shared-features';
import { orderDeliveryShareApi } from '../../api/orderDeliveryShareApi';
import { DeliveryForm } from '../../components/share/DeliveryForm';
import { DeliverySummary, type SummaryPhoto } from '../../components/share/DeliverySummary';
import {
  MSG,
  PHOTO_MAX,
  buildDeliveryPayload,
  expiredDescription,
  fatalScreenOf,
  fieldErrorOf,
  initialFormValues,
  interpretPhotoUpload,
  isFormDirty,
  itemLines,
  parseDeliveryView,
  photoFailText,
  photoLabel,
  submitBlockLabel,
  validateDelivery,
  type DeliveryFieldErrors,
  type DeliveryFieldKey,
  type DeliveryFormValues,
  type DeliveryInfo,
  type DeliveryView,
  type FatalScreen,
} from './vendorDelivery.logic';
import { LIST_PATH, parseOrderVendorId } from './vendorOrderList.logic';

/**
 * C-4-1 거래처 배송정보 등록 (외부·무인증 링크 화면, `share/SUPPLIER_DELIVERY`).
 * 자격증명은 공유 쿠키(share_jwt)뿐이며 화면은 토큰·shareId를 다루지 않는다(F-04 §9).
 * 화면 모드는 서버 B3 응답으로만 결정: OPEN=등록(POST), REPLIED=보기(+수정 PUT).
 */

function Shell({ children }: { children: ReactNode }) {
  return (
    <div data-group="03" className="min-h-screen" style={{ background: 'var(--shell-bg)' }}>
      <div className="mx-auto flex min-h-screen w-full max-w-[480px] flex-col gap-3 p-4" style={{ background: 'var(--shell-bg)' }}>
        {children}
      </div>
    </div>
  );
}

export function VendorDeliveryPage() {
  useEffect(() => {
    document.title = MSG.documentTitle;
  }, []);

  const res = useShareResource((signal) => orderDeliveryShareApi.get(signal));

  if (res.status === 'loading') {
    return (
      <Shell>
        <ShareLoadSkeleton />
      </Shell>
    );
  }

  if (res.status === 'error' || res.data === undefined) {
    const e = res.error;
    const fatal = e ? fatalScreenOf(e) : null;
    if (fatal === 'RECEIVED') {
      return (
        <Shell>
          <ReceivedBody />
        </Shell>
      );
    }
    if (fatal) return <ShareStateScreen kind={fatal} />;
    const kind = e?.kind === 'NETWORK' || e?.kind === 'SERVER' ? e.kind : 'UNKNOWN';
    return <ShareStateScreen kind={kind} onRetry={res.reload} />;
  }

  const parsed = parseDeliveryView(res.data);
  if (!parsed.ok) {
    if (parsed.screen === 'EXPIRED') {
      return <ExpiredScreen companyName={parsed.companyName} companyPhone={parsed.companyPhone} deliveryDate={parsed.deliveryDate} />;
    }
    return (
      <ShareStateScreen
        kind={parsed.screen}
        companyName={parsed.companyName}
        onRetry={parsed.screen === 'UNKNOWN' ? res.reload : undefined}
      />
    );
  }
  return <DeliveryScreen view={parsed.view} />;
}

/** C-4-1 ④ 만료 화면 — 입고일 날짜와 문의처(사업장명·연락처)를 함께 보여준다. 값이 없는 줄은 숨긴다. */
function ExpiredScreen({ companyName, companyPhone, deliveryDate }: { companyName?: string; companyPhone?: string; deliveryDate?: string }) {
  const hasInquiry = Boolean(companyName || companyPhone);
  return (
    <ShareNoticeScreen icon={<IconInfoCircle />} title={MSG.expiredTitle} description={expiredDescription(deliveryDate, formatMonthDayDow)}>
      {hasInquiry && (
        <p className="flex flex-wrap items-center justify-center gap-x-2 text-sm text-text-secondary">
          <span>{MSG.inquiryLabel}</span>
          {companyName && <span>{companyName}</span>}
          {companyPhone && (
            <TextLink onClick={() => window.location.assign(`tel:${companyPhone.replace(/[^0-9+]/g, '')}`)}>{companyPhone}</TextLink>
          )}
        </p>
      )}
    </ShareNoticeScreen>
  );
}

function ReceivedBody() {
  return (
    <>
      <h1 tabIndex={-1} className="text-title-sm font-bold text-text-primary">
        {MSG.title}
      </h1>
      <Alert kind="info" aria-live="polite">
        {MSG.received}
      </Alert>
    </>
  );
}

// ───────────── 사진 큐 ─────────────

interface PhotoEntry {
  key: string;
  status: SharePhotoItem['status'];
  progress?: number;
  file?: File;
  orderPhotoId?: number;
  preview?: string | null;
  errorText?: string;
  fromServer: boolean;
}

type Banner = { kind: 'info' | 'warning'; text: string } | null;

const FIELD_FOCUS_ID: Record<DeliveryFieldKey, string> = {
  arrivalDate: 'delivery-arrival-date',
  arrivalTime: 'delivery-arrival-time-h',
  driverName: 'delivery-driver-name',
  driverPhone: 'delivery-driver-phone',
  issueYn: 'delivery-issue-label',
  issueNote: 'delivery-issue-note',
};
const FIELD_ORDER = Object.keys(FIELD_FOCUS_ID) as DeliveryFieldKey[];

function serverPhotos(view: DeliveryView): PhotoEntry[] {
  return view.photos.map((p) => ({ key: `s${p.orderPhotoId}`, status: 'done', orderPhotoId: p.orderPhotoId, fromServer: true }));
}

function DeliveryScreen({ view }: { view: DeliveryView }) {
  const navigate = useNavigate();
  // 받은 발주 목록에서 들어온 화면이면 [목록으로] 복귀를 보여준다.
  const fromList = useMemo(() => parseOrderVendorId(window.location.search) !== null, []);
  const [status, setStatus] = useState<DeliveryView['state']>(view.state);
  const [editable, setEditable] = useState(view.editable);
  const [info, setInfo] = useState<DeliveryInfo | null>(view.delivery);
  const [editing, setEditing] = useState(view.state === 'OPEN' && view.editable);
  const [overlay, setOverlay] = useState<Exclude<FatalScreen, 'RECEIVED'> | 'UNKNOWN' | null>(null);
  const goFatal = (f: FatalScreen) => {
    if (f === 'RECEIVED') {
      setStatus('RECEIVED');
      setEditing(false);
    } else setOverlay(f);
  };
  const [sent, setSent] = useState<{ changed: boolean | null } | null>(null);
  const [banner, setBanner] = useState<Banner>(null);

  const [values, setValues] = useState<DeliveryFormValues>(() => initialFormValues(view.delivery));
  const [baseline, setBaseline] = useState<DeliveryFormValues>(() => initialFormValues(view.delivery));
  const [serverErrors, setServerErrors] = useState<DeliveryFieldErrors>({});
  const [attempted, setAttempted] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const submittingRef = useRef(false);

  const [photos, setPhotos] = useState<PhotoEntry[]>(() => serverPhotos(view));
  const [photoNotice, setPhotoNotice] = useState<string | null>(null);
  const [limitReached, setLimitReached] = useState(false);
  const [viewerIndex, setViewerIndex] = useState<number | null>(null);
  const localSeq = useRef(0);
  const previews = useRef<string[]>([]);

  useEffect(
    () => () => {
      previews.current.forEach((u) => URL.revokeObjectURL(u));
    },
    [],
  );

  const photoBusy = photos.some((p) => p.status === 'queued' || p.status === 'uploading');
  const failedCount = photos.filter((p) => p.status === 'failed').length;

  // ── 이탈 보호: 편집 중 업로드 진행·미제출 사진·입력 변경이 있을 때 ──
  const guard = editing && (photoBusy || photos.some((p) => !p.fromServer) || isFormDirty(values, baseline));
  useEffect(() => {
    if (!guard) return;
    const onBefore = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', onBefore);
    return () => window.removeEventListener('beforeunload', onBefore);
  }, [guard]);

  // ── 상태 전환 시 제목으로 포커스 이동 ──
  const phaseKey = `${overlay}|${status}|${editing}|${sent ? 1 : 0}`;
  useEffect(() => {
    document.querySelector<HTMLElement>('h1')?.focus();
  }, [phaseKey]);

  // ── 사진 업로드 큐: 한 번에 한 장씩 순차 ──
  const patchPhoto = useCallback((key: string, patch: Partial<PhotoEntry>) => {
    setPhotos((list) => list.map((p) => (p.key === key ? { ...p, ...patch } : p)));
  }, []);

  const failPhoto = useCallback(
    (key: string, code: string | undefined) => {
      if (code === 'ORDER_PHOTO_LIMIT') {
        // 서버 건당 한도 도달 — 이 항목은 버리고 추가 비활성
        setPhotos((list) => list.filter((p) => p.key !== key));
        setLimitReached(true);
        setPhotoNotice(MSG.photoLimit);
        return;
      }
      patchPhoto(key, { status: 'failed', errorText: photoFailText(code), progress: undefined });
    },
    [patchPhoto],
  );

  useEffect(() => {
    if (photos.some((p) => p.status === 'uploading')) return;
    const next = photos.find((p) => p.status === 'queued' && p.file);
    if (!next || !next.file) return;
    const { key, file } = next;
    patchPhoto(key, { status: 'uploading', progress: 0, errorText: undefined });
    orderDeliveryShareApi
      .uploadPhoto(file, (pct) => patchPhoto(key, { progress: pct }))
      .then((data) => {
        const r = interpretPhotoUpload(data);
        if (r.ok) patchPhoto(key, { status: 'done', orderPhotoId: r.photo.orderPhotoId, progress: undefined, errorText: undefined });
        else failPhoto(key, r.code);
      })
      .catch((err) => {
        const c = classifyShareError(err);
        const fatal = fatalScreenOf(c);
        if (fatal) {
          goFatal(fatal);
          return;
        }
        failPhoto(key, c.kind === 'PAYLOAD' ? 'PAYLOAD_TOO_LARGE' : c.code);
      });
  }, [photos, patchPhoto, failPhoto]);

  const addFiles = (files: File[]) => {
    setPhotoNotice(null);
    setPhotos((list) => [
      ...list,
      ...files.map((file): PhotoEntry => {
        const preview = URL.createObjectURL(file);
        previews.current.push(preview);
        localSeq.current += 1;
        return { key: `l${localSeq.current}`, status: 'queued', file, preview, fromServer: false };
      }),
    ]);
  };

  const removePhoto = async (key: string) => {
    const entry = photos.find((p) => p.key === key);
    if (!entry) return;
    setPhotoNotice(null);
    if (entry.orderPhotoId !== undefined) {
      try {
        await orderDeliveryShareApi.deletePhoto(entry.orderPhotoId);
      } catch (err) {
        const fatal = fatalScreenOf(classifyShareError(err));
        if (fatal) goFatal(fatal);
        else setPhotoNotice(MSG.photoDeleteFail);
        return;
      }
      setLimitReached(false);
    }
    if (entry.preview) URL.revokeObjectURL(entry.preview);
    setPhotos((list) => list.filter((p) => p.key !== key));
  };

  const retryPhoto = (key: string) => patchPhoto(key, { status: 'queued', errorText: undefined });

  const doneIds = photos.filter((p) => p.status === 'done' && p.orderPhotoId !== undefined);
  const viewerItems = doneIds.map((p, i) => ({
    id: p.key,
    src: guestFileUrl(orderDeliveryShareApi.photoPath(p.orderPhotoId as number, 'original')),
    alt: photoLabel(i),
  }));
  const summaryPhotos: SummaryPhoto[] = doneIds.map((p, i) => ({
    key: p.key,
    label: photoLabel(i),
    src: guestFileUrl(orderDeliveryShareApi.photoPath(p.orderPhotoId as number, 'thumb')),
  }));
  const photoItems: SharePhotoItem[] = photos.map((p, i) => ({
    key: p.key,
    label: photoLabel(i),
    status: p.status,
    progress: p.progress,
    previewSrc:
      p.orderPhotoId !== undefined
        ? guestFileUrl(orderDeliveryShareApi.photoPath(p.orderPhotoId, 'thumb'))
        : (p.preview ?? null),
    errorText: p.errorText,
  }));
  const openPhoto = (key: string) => {
    const idx = doneIds.findIndex((p) => p.key === key);
    if (idx >= 0) setViewerIndex(idx);
  };

  // ── 서버 값으로 화면 전체 재설정 ──
  const applyView = (v: DeliveryView, b: Banner) => {
    const init = initialFormValues(v.delivery);
    setStatus(v.state);
    setEditable(v.editable);
    setInfo(v.delivery);
    setEditing(v.state === 'OPEN' && v.editable);
    setValues(init);
    setBaseline(init);
    setServerErrors({});
    setAttempted(false);
    setFormError(null);
    setPhotos(serverPhotos(v));
    setPhotoNotice(null);
    setLimitReached(false);
    setSent(null);
    setBanner(b);
  };

  /** B3 재조회. 성공하면 화면 모델 반환, 상태 오류는 전체 화면 전환 후 null. */
  const reloadFromServer = async (b: Banner): Promise<DeliveryView | null> => {
    try {
      const parsed = parseDeliveryView(await orderDeliveryShareApi.get());
      if (!parsed.ok) {
        if (parsed.screen === 'CLOSED' || parsed.screen === 'EXPIRED') setOverlay(parsed.screen);
        else setFormError(MSG.submitFail);
        return null;
      }
      applyView(parsed.view, b);
      return parsed.view;
    } catch (err) {
      const fatal = fatalScreenOf(classifyShareError(err));
      if (fatal) goFatal(fatal);
      else setFormError(MSG.submitFail);
      return null;
    }
  };

  // ── 입력 ──
  const clientErrors = validateDelivery(values);
  const shownErrors: DeliveryFieldErrors = attempted
    ? { ...clientErrors, ...serverErrors }
    : { ...(values.arrivalDate ? { arrivalDate: clientErrors.arrivalDate } : {}), ...serverErrors };

  const onChange = (patch: Partial<DeliveryFormValues>) => {
    setValues((v) => ({ ...v, ...patch }));
    setServerErrors((e) => {
      const next = { ...e };
      (Object.keys(patch) as DeliveryFieldKey[]).forEach((k) => delete next[k]);
      return next;
    });
  };

  const isEdit = status === 'REPLIED';

  const submit = async () => {
    if (submittingRef.current) return;
    if (submitBlockLabel(values, photoBusy) !== null) return;
    if (Object.keys(clientErrors).length > 0) {
      setAttempted(true);
      const first = FIELD_ORDER.find((k) => clientErrors[k]);
      if (first) document.getElementById(FIELD_FOCUS_ID[first])?.focus();
      return;
    }
    submittingRef.current = true;
    setSubmitting(true);
    setFormError(null);
    try {
      const body = buildDeliveryPayload(values);
      const raw = (isEdit ? await orderDeliveryShareApi.update(body) : await orderDeliveryShareApi.create(body)) as
        | Record<string, unknown>
        | undefined;
      onSubmitted(raw);
    } catch (err) {
      await onSubmitError(err);
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  const onSubmitted = (raw: Record<string, unknown> | undefined) => {
    const d = raw ?? {};
    const arrivalDate = typeof d.arrivalDate === 'string' ? d.arrivalDate : null;
    const arrivalTime = typeof d.arrivalTime === 'string' ? d.arrivalTime : null;
    if (!arrivalDate || !arrivalTime) {
      // 응답 요약이 비정상 — 서버 값으로 다시 그림(제출 자체는 성공)
      void reloadFromServer(null).then((v) => v && setSent({ changed: null }));
      return;
    }
    if (typeof d.photoCnt !== 'number' || typeof d.replyRound !== 'number') {
      // 필수값 누락 — 0 등으로 대체하지 않고 응답 형식 오류로 처리
      setOverlay('UNKNOWN');
      return;
    }
    const next: DeliveryInfo = {
      arrivalDate: arrivalDate.slice(0, 10),
      arrivalTime: arrivalTime.slice(0, 5),
      driverName: values.driverName.trim(),
      driverPhone: values.driverPhone,
      issueYn: values.issueYn,
      issueNote: values.issueYn === 'Y' ? values.issueNote.trim() : '',
      photoCnt: d.photoCnt,
      replyRound: d.replyRound,
    };
    setInfo(next);
    setStatus('REPLIED');
    setEditing(false);
    setSent({ changed: typeof d.changed === 'boolean' ? d.changed : null });
    setBanner(null);
    setAttempted(false);
    setServerErrors({});
    // 서버에 없는 실패 항목은 정리
    setPhotos((list) => list.filter((p) => p.status === 'done').map((p) => ({ ...p, fromServer: true })));
    const init = initialFormValues(next);
    setValues(init);
    setBaseline(init);
  };

  const onSubmitError = async (err: unknown) => {
    const c = classifyShareError(err);
    const fatal = fatalScreenOf(c);
    if (fatal === 'RECEIVED') {
      setStatus('RECEIVED');
      setEditing(false);
      return;
    }
    if (fatal) {
      setOverlay(fatal);
      return;
    }
    if (c.code === 'ALREADY_RESPONDED') {
      await reloadFromServer({ kind: 'info', text: MSG.alreadyReplied });
      return;
    }
    if (c.code === 'ORDER_DELIVERY_NOT_FOUND') {
      await reloadFromServer({ kind: 'warning', text: MSG.deliveryNotFound });
      return;
    }
    if (c.kind === 'VALIDATION') {
      const fe = fieldErrorOf(c);
      if (fe) {
        setServerErrors({ [fe.field]: fe.text });
        document.getElementById(FIELD_FOCUS_ID[fe.field])?.focus();
      } else {
        setFormError(MSG.checkInput);
      }
      return;
    }
    if (c.kind === 'NETWORK') {
      // 결과를 모름. POST는 B3 재조회로 접수 여부 확인, PUT은 같은 값 재전송이 안전하므로 재시도 허용.
      if (!isEdit) {
        // 접수 여부만 확인 — 입력·사진은 지우지 않음(REPLIED일 때만 서버 값으로 전환)
        try {
          const parsed = parseDeliveryView(await orderDeliveryShareApi.get());
          if (parsed.ok && parsed.view.state === 'REPLIED') {
            applyView(parsed.view, null);
            setSent({ changed: null });
            return;
          }
          if (parsed.ok && parsed.view.state === 'RECEIVED') {
            goFatal('RECEIVED');
            return;
          }
          if (!parsed.ok && (parsed.screen === 'CLOSED' || parsed.screen === 'EXPIRED')) {
            setOverlay(parsed.screen);
            return;
          }
        } catch (err2) {
          const fatal2 = fatalScreenOf(classifyShareError(err2));
          if (fatal2) {
            goFatal(fatal2);
            return;
          }
        }
        setFormError(MSG.submitFail);
        return;
      }
      setFormError(MSG.submitFail);
      return;
    }
    setFormError(c.kind === 'SERVER' ? MSG.serverBusy : MSG.checkInput);
  };

  const startEdit = () => {
    const init = initialFormValues(info);
    setValues(init);
    setBaseline(init);
    setServerErrors({});
    setAttempted(false);
    setFormError(null);
    setSent(null);
    setBanner(null);
    setEditing(true);
  };

  const cancelEdit = () => {
    const init = initialFormValues(info);
    setValues(init);
    setBaseline(init);
    setFormError(null);
    setAttempted(false);
    setServerErrors({});
    setPhotos((list) => list.filter((p) => p.status === 'done'));
    setEditing(false);
  };

  // ───────────── 렌더 ─────────────

  if (overlay) {
    if (overlay === 'EXPIRED') {
      return <ExpiredScreen companyName={view.companyName || undefined} companyPhone={view.companyPhone || undefined} deliveryDate={view.deliveryDate || undefined} />;
    }
    return <ShareStateScreen kind={overlay} companyName={view.companyName || undefined} />;
  }

  const subtitle = [view.companyName, view.vendorName].filter(Boolean).join(' · ');
  const lines = itemLines(view);

  return (
    <Shell>
      <header>
        {fromList && <TextLink onClick={() => navigate(LIST_PATH)}>{MSG.backToList}</TextLink>}
        <h1 tabIndex={-1} className={`text-title-sm font-bold text-text-primary${fromList ? ' mt-1' : ''}`}>
          {MSG.title}
        </h1>
        {subtitle && <p className="mt-1 text-body-sm text-text-secondary">{subtitle}</p>}
      </header>

      <Card>
        <dl className="flex flex-col gap-2">
          {view.siteLabel && <InfoRow label={MSG.cardSite}>{view.siteLabel}</InfoRow>}
          {view.workTypeName && <InfoRow label={MSG.cardWorkType}>{view.workTypeName}</InfoRow>}
          {lines.length > 0 && (
            <InfoRow label={MSG.cardItems}>
              {lines.map((l, i) => (
                <div key={i}>{l}</div>
              ))}
            </InfoRow>
          )}
          <InfoRow label={MSG.cardDeliveryDate}>{formatMonthDayDow(view.deliveryDate)}</InfoRow>
          {view.deliveryAddress && <InfoRow label={MSG.cardAddress}>{view.deliveryAddress}</InfoRow>}
        </dl>
      </Card>

      {banner && <Alert kind={banner.kind}>{banner.text}</Alert>}

      {status === 'RECEIVED' && (
        <>
          {info && <DeliverySummary info={info} photos={summaryPhotos} onOpenPhoto={setViewerIndex} />}
          <Alert kind="info" aria-live="polite">
            {MSG.received}
          </Alert>
        </>
      )}

      {status === 'OPEN' && !editable && <Alert kind="info">{MSG.notEditable}</Alert>}

      {editing && editable && status !== 'RECEIVED' && (
        <DeliveryForm
          mode={isEdit ? 'edit' : 'create'}
          values={values}
          errors={shownErrors}
          deliveryDate={view.deliveryDate}
          formError={formError}
          photoNotice={photoNotice}
          photoItems={photoItems}
          photoDisabled={limitReached || photos.length >= PHOTO_MAX}
          failedPhotoCount={failedCount}
          blockLabel={submitBlockLabel(values, photoBusy)}
          submitting={submitting}
          onChange={onChange}
          onSubmit={() => void submit()}
          onCancel={isEdit ? cancelEdit : undefined}
          onAddFiles={addFiles}
          onRemovePhoto={(k) => void removePhoto(k)}
          onRetryPhoto={retryPhoto}
          onOpenPhoto={openPhoto}
          onPhotoNotice={setPhotoNotice}
        />
      )}

      {status === 'REPLIED' && !editing && info && (
        <>
          {sent && (
            <Alert kind="success" title={MSG.sentTitle} aria-live="polite">
              {MSG.sentDesc}
            </Alert>
          )}
          {sent?.changed === false && <Alert kind="info">{MSG.unchanged}</Alert>}
          <DeliverySummary info={info} photos={summaryPhotos} onOpenPhoto={setViewerIndex} />
          {editable ? (
            <>
              <p className="text-body-sm text-text-secondary">{MSG.repliedGuide}</p>
              <Button size="lg" variant="secondary" className="w-full" onClick={startEdit}>
                {MSG.btnStartEdit}
              </Button>
            </>
          ) : (
            <Alert kind="info">{MSG.notEditable}</Alert>
          )}
        </>
      )}

      <PhotoViewer
        open={viewerIndex !== null && viewerItems.length > 0}
        items={viewerItems}
        index={viewerIndex ?? 0}
        onIndexChange={setViewerIndex}
        title={MSG.viewerTitle}
        onClose={() => setViewerIndex(null)}
      />
    </Shell>
  );
}

function InfoRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex gap-3">
      <dt className="w-16 shrink-0 text-body-sm font-semibold text-text-muted">{label}</dt>
      <dd className="min-w-0 flex-1 text-body-sm text-text-primary">{children}</dd>
    </div>
  );
}
